import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Form } from 'react-bootstrap';
import { config } from '../constants.js';
import { useAppTheme, useThemeHelpers } from '../../hooks/useAppTheme.js';
import SchoolSelector from '../school/SchoolSelector.js';
import '../../styles/teacherOnboarding.big-top.scss';

const API_URL = config.url.API_URL;

const TEACHER_ONBOARDING_COPY = (
  'Your account is a free student account with a 14-day trial while we review your teacher request. '
  + 'If approved, you will receive complimentary teacher access. '
  + 'After you submit, we will send a verification link to your school email. '
  + 'Click that link to verify your email and finish your request. '
  + 'You will receive another message at that address when your request has been reviewed. Thank you for joining!'
);

export default function TeacherOnboarding({ userId }) {
  const navigate = useNavigate();
  const theme = useAppTheme();
  const { isThemed, pageClass } = useThemeHelpers(theme);
  const pageThemeClass = pageClass('teacherOnboarding');
  const shellClass = pageThemeClass ? `teacher-onboarding ${pageThemeClass}` : 'teacher-onboarding';

  const [schoolName, setSchoolName] = useState('');
  const [schoolId, setSchoolId] = useState(null);
  const [schoolEmail, setSchoolEmail] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!userId) {
      return undefined;
    }

    let cancelled = false;

    async function loadProfileSchool() {
      try {
        const response = await fetch(`${API_URL}/users/fetchUserInfoById/${userId}`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
        if (!response.ok) {
          return;
        }
        const userData = await response.json();
        const nextSchoolId = userData.profile?.schoolId || userData.teacherRequest?.schoolId || null;
        const nextSchoolName = userData.profile?.schoolName || userData.teacherRequest?.schoolName || '';
        if (cancelled || (!nextSchoolId && !nextSchoolName)) {
          return;
        }
        setSchoolId((current) => current || nextSchoolId);
        setSchoolName((current) => (current?.trim() ? current : nextSchoolName));
      } catch (loadError) {
        console.error('Error loading profile school:', loadError);
      }
    }

    loadProfileSchool();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/teacher-request`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ schoolName, schoolId: schoolId || null, schoolEmail }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.message || 'Unable to submit teacher request.');
      }

      sessionStorage.removeItem('accountIntent');
      navigate('/sectionHome', {
        replace: true,
        state: {
          teacherRequestSubmitted: true,
          teacherVerificationSent: data.verificationEmailSent !== false,
        },
      });
    } catch (submitError) {
      setError(submitError.message || 'Unable to submit teacher request.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={shellClass}>
      <div className="teacher-onboarding__card">
        <h1 className="teacher-onboarding__title">Request teacher access</h1>
        <p className="teacher-onboarding__copy">{TEACHER_ONBOARDING_COPY}</p>

        {error && <Alert variant="danger">{error}</Alert>}

        <Form onSubmit={handleSubmit}>
          <SchoolSelector
            id="teacherSchoolName"
            label="School name"
            required
            value={{ schoolId, schoolName }}
            onChange={(school) => {
              setSchoolId(school.schoolId);
              setSchoolName(school.schoolName || '');
            }}
          />

          <Form.Group className="mb-4" controlId="teacherSchoolEmail">
            <Form.Label>School email</Form.Label>
            <Form.Control
              type="email"
              value={schoolEmail}
              onChange={(event) => setSchoolEmail(event.target.value)}
              required
              maxLength={254}
              autoComplete="email"
            />
          </Form.Group>

          <div className="teacher-onboarding__actions">
            <Button type="submit" variant="primary" className="teacher-onboarding__submit-btn" disabled={submitting || !schoolName.trim() || !schoolEmail.trim()}>
              {submitting ? 'Submitting…' : 'Submit request'}
            </Button>
            <Button
              type="button"
              variant="outline-secondary"
              disabled={submitting}
              onClick={() => navigate('/sectionHome', { replace: true })}
            >
              Skip for now
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
}
